import { Fragment, useState } from 'react'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { TooltipProvider } from '@/components/ui/tooltip'
import type { UtilizationLine } from '../types'
import { HeaderCell } from './HeaderCell'
import { PeriodTitle } from './PeriodTitle'
import { UtilizationRow, rowDomId } from './UtilizationRow'
import { UtilizationTotalRow } from './UtilizationTotalRow'
import { COST_COLUMN_DIVIDER, COUNT_COLUMN } from './utilizationClasses'

/** Which dimension the receipt is grouped by. `category` is the shape the
 *  customer is invoiced in; `duty` answers what each duty cost. */
export type UtilizationGrouping = 'category' | 'duty'

const GROUPING_LABELS: Record<UtilizationGrouping, string> = {
  duty: 'By duty',
  category: 'By category',
}

/** Tab order, which is fixed: the invoice shape reads first, the duty
 *  breakdown second. Stated explicitly rather than left to key order. */
const GROUPING_ORDER: UtilizationGrouping[] = ['category', 'duty']

interface UtilizationTableProps {
  lines: UtilizationLine[]
  totalMinutes: number
  totalUsd: number
  asOfDate: Date
  isCurrent: boolean
  grouping: UtilizationGrouping
  onGroupingChange: (grouping: UtilizationGrouping) => void
  /** The per-duty fetch failed. Without saying so, a duty view with no
   *  duty rows reads exactly like an org that has none. */
  dutyCostsFailed?: boolean
}

export function UtilizationTable({
  lines,
  totalMinutes,
  totalUsd,
  asOfDate,
  isCurrent,
  grouping,
  onGroupingChange,
  dutyCostsFailed = false,
}: UtilizationTableProps) {
  // What the reader has toggled, keyed by the row's whole path so the map
  // survives data refreshes. A path absent from the map falls back to the
  // default for its position.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})

  // Every group starts folded, so the receipt opens as one list of lines at
  // one level and the reader opens the one they are asking about. A duty
  // view with every breakdown open buries what each duty cost, and a Calls
  // group open by default pushes the lines under it off the first read.
  const startsFolded = true

  // Flipped against the value on screen rather than against `prev[path]`,
  // which is undefined until the row is first toggled and would make that
  // first click a no-op on anything that starts folded.
  const toggle = (path: string, isCollapsed: boolean) =>
    setCollapsed((prev) => ({ ...prev, [path]: !isCollapsed }))

  /** Render a line and, when expanded, everything beneath it. Any line with
   *  children is collapsible, so a duty's Calls group folds away
   *  independently of the duty itself. */
  const renderRows = (
    rows: UtilizationLine[],
    depth: number,
    parentPath: string
  ): React.ReactNode =>
    rows.map((line) => {
      // Keyed on the row's own id where it has one: duty names are
      // user-controlled and two duties can share one, which would give
      // both rows the same React key, collapse entry and DOM id.
      const path = `${parentPath}/${line.rowId ?? line.description}`
      const children = line.subRows ?? []
      // A group holding the Connection estimate opens instead: the estimate
      // is the one line in it close has yet to settle, and a line behind a
      // chevron is one the reader never sees. The printed receipt keeps the
      // fold the screen has.
      const isCollapsed =
        collapsed[path] ??
        (startsFolded && !children.some((child) => child.isProjected))
      const childIds = children
        .map((child) => rowDomId(`${path}/${child.description}`))
        .join(' ')
      return (
        <Fragment key={path}>
          <UtilizationRow
            line={line}
            depth={depth}
            path={path}
            isCollapsed={isCollapsed}
            childIds={childIds}
            onToggle={() => toggle(path, isCollapsed)}
          />
          {!isCollapsed && renderRows(children, depth + 1, path)}
        </Fragment>
      )
    })

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs font-semibold tracking-widest text-muted-foreground uppercase">
          <PeriodTitle
            title="Utilization"
            isCurrent={isCurrent}
            asOfDate={asOfDate}
          />
        </div>
        <div className="flex items-center gap-3">
          <ToggleGroup
            type="single"
            aria-label="Group utilization by"
            value={grouping}
            onValueChange={(next) =>
              next && onGroupingChange(next as UtilizationGrouping)
            }
            className="gap-0.5 rounded-lg bg-muted p-0.5">
            {GROUPING_ORDER.map((value) => (
              <ToggleGroupItem
                key={value}
                value={value}
                className="h-6 rounded-md px-2 text-xs data-[state=on]:bg-card data-[state=on]:shadow-sm">
                {GROUPING_LABELS[value]}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      </div>
      {dutyCostsFailed && grouping === 'duty' && (
        <div
          role="status"
          className="mb-3 text-xs text-muted-foreground border border-dashed border-border rounded-md px-3 py-2">
          Per-duty breakdown unavailable — the charges below are the
          period&rsquo;s totals, not a duty split.
        </div>
      )}
      <TooltipProvider delayDuration={150}>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] text-muted-foreground border-b border-border">
              <HeaderCell align="left">Description</HeaderCell>
              <HeaderCell align="right" className={COUNT_COLUMN}>
                Count
              </HeaderCell>
              <HeaderCell align="right" className={COST_COLUMN_DIVIDER}>
                Minutes
              </HeaderCell>
              <HeaderCell align="right">Price ($/min)</HeaderCell>
              <HeaderCell align="right">Amount ($)</HeaderCell>
            </tr>
          </thead>
          <tbody>
            {renderRows(lines, 0, 'utilization')}
            <UtilizationTotalRow
              totalMinutes={totalMinutes}
              totalUsd={totalUsd}
            />
          </tbody>
        </table>
      </TooltipProvider>
    </div>
  )
}
